import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CompareArrows } from '@mui/icons-material'
import { Box, Button, Stack } from '@mui/material'

import { Game } from '../../../../../features/game/types/Game'
import { useGetInternalGameDocumentQuery } from '../../../../api/games'
import { InternalGameSelectorDialog } from '../../../../components/InternalGameSelectorDialog/InternalGameSelectorDialog'
import { maxBenchmarks } from '../../helpers/analysisOverviewHelpers'
import { Benchmark } from '../../types/InternalAnalysis'
import { BenchmarkGameBlock } from '../BenchmarkGameBlock/BenchmarkGameBlock'

type BenchmarkGamesProps = {
  benchmarks: Benchmark[]
  disabled?: boolean
  onPick: (benchmarks: Benchmark[]) => void
  onRemove: (index: number) => void
}

export const BenchmarkGames: FC<BenchmarkGamesProps> = ({ benchmarks, disabled, onPick, onRemove }) => {
  const { t } = useTranslation()
  const [isSelectorOpen, setIsSelectorOpen] = useState(false)
  // A fixed number of reads (the benchmark maximum), for the selector's chips; the blocks share the cache.
  const first = useGetInternalGameDocumentQuery(benchmarks[0]?.gameId ?? '', { skip: !benchmarks[0] })
  const second = useGetInternalGameDocumentQuery(benchmarks[1]?.gameId ?? '', { skip: !benchmarks[1] })
  const third = useGetInternalGameDocumentQuery(benchmarks[2]?.gameId ?? '', { skip: !benchmarks[2] })
  const loadedNames = [first.data?.name, second.data?.name, third.data?.name]
  // The selector reads these only when it opens.
  const selectedGames = benchmarks.map(({ gameId }, index) => new Game({ id: gameId, name: loadedNames[index] ?? gameId } as Game))
  const selectedMarketIsos = Object.fromEntries(benchmarks.map(({ gameId, marketIso }) => [gameId, marketIso]))

  const handleDone = (games: Game[], marketIsos: Record<string, string>) => {
    setIsSelectorOpen(false)
    onPick(games.map(({ id }) => ({ gameId: id, marketIso: marketIsos[id] })))
  }

  return (
    <Stack spacing={1}>
      <Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<CompareArrows />}
          disabled={disabled || benchmarks.length >= maxBenchmarks}
          onClick={() => setIsSelectorOpen(true)}
        >
          {t('internal-analysts:analysis_overview_benchmark_game')}
        </Button>
      </Box>
      {benchmarks.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={2}>
          {benchmarks.map((benchmark, index) => (
            <BenchmarkGameBlock
              key={`${benchmark.gameId}-${benchmark.marketIso}`}
              benchmark={benchmark}
              index={index}
              max={maxBenchmarks}
              onRemove={() => onRemove(index)}
            />
          ))}
        </Stack>
      )}
      <InternalGameSelectorDialog
        open={isSelectorOpen}
        title={t('internal-analysts:analysis_overview_benchmark_select')}
        selectedGames={selectedGames}
        selectedMarketIsos={selectedMarketIsos}
        maxSelected={maxBenchmarks}
        onClose={() => setIsSelectorOpen(false)}
        onDone={handleDone}
      />
    </Stack>
  )
}
